import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validator, Validators} from '@angular/forms';
import { OptionService } from "../option/option.service";
import { Donation } from "../donation/donation";
import {UrlService} from "../url/url.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: 'amount',
    templateUrl: './amount.component.html'
})
export class AmountComponent implements OnInit {
    /**
     * Speed of valuechanges of the slider
     */
    static SLIDER_SPEED = 2.5;

    /**
     * Minimum value from which on the non-linear slider is used.
     */
    static SLIDER_NON_LINEAR_MIN = 200;

    /**
     * general max of the slider
     */
    static SLIDER_END_GENERAL = 15000000;

    /**
     * Maximum max in the shop
     */
    static SHOP_MAX = 14999;

    /**
     * The current donation object
     */
    @Input() donation: Donation;

    /**
     * the formGroup for validation
     */
    @Input() form: FormGroup;

    /**
     * minimum amount
     * overwrites the option value and url param if set
     */
    @Input() minAmount: number;

    /**
     * Dom Element with the infotext
     */
    @ViewChild('amountInfotext', { static: false }) amountInfotext: ElementRef;

    /**
     * Dom element with the transaction amount
     */
    @ViewChild('transactionAmount', { static: false }) transactionAmount: ElementRef;

    /**
     * Holds the internal slider value. This can be different than the amount of the donation
     */
    sliderValue: number;

    /**
     * Define start and end of the slider
     */
    sliderStart = 5;
    sliderEnd = 500;

    /**
     * Defines if the infotext should be shown
     */
    showInfotext = false;

    /**
     * Textkey for the infotext that is shown if the value is higher/lower than max or higher that absolut max
     */
    infoTextTextkey = 'amount.info';

    /**
     * Should the infotext be disabled in general
     */
    disableInfotext = false;

    /**
     * CSS height for the infotext
     * @type {string}
     */
    infotextHeight = "0px";

    /**
     * Should buttons be available for the amount selection
     */
    hasButtons = false;

    /**
     * Configuration of the buttons, if available
     */
    buttonConfig: any = null;

    /**
     * Show decimals in the buttons
     */
    showDecimals = false;

    /**
     * Show larger amounts in the buttons
     */
    hasLargeAmount = false;

    /**
     * should the slider be visible or not
     */
    showSlider = true;

    /**
     * Toggle for the "more" button when buttons are active
     */
    showMore = true;

    /**
     * textkey for the amount
     */
    labelTextkey = 'amount.label';

    /**
     * error textkey for the amount
     */
    errorTextkey = 'amount.error';

    /**
     * Holds the type of the project
     */
    projectType: string = null;

    /**
     * Defines the absolute maximum of the amount that should be selectable
     */
    absolutMaximum: number;

    /**
     * The constructor
     * @param {OptionService} optionService
     * @param {UrlService} urlService
     * @param {TranslateService} translate
     */
    constructor(private optionService: OptionService, private urlService: UrlService, private translate: TranslateService) {}

    /**
     * Sets the selected amount of the button to a real float value
     * @param amount
     */
    setButtonAmount(amount: any): void {
        this.donation.amount = amount * 1;

        // update the slider position.
        this.updateSliderposition();
    }

    /**
     * Initialize the slider
     */
    public ngOnInit(): any {
        // retrieve the button config
        this.hasButtons = this.optionService.getOption('has_buttons');
        this.showMore = !this.optionService.getOption('has_no_slider');

        // get the type of the project
        this.projectType = this.optionService.getProjectType();

        // set label textkey depending on the type of the project
        if(this.projectType === 'membership') {
            this.labelTextkey = 'amount.label.membership';
        }

        // get the absolut amount maximum
        this.absolutMaximum = this.optionService.getOption('donation_value_absolute_max');
        if (!this.absolutMaximum) {
          // fallback to fix value if not defined in the project
          this.absolutMaximum = AmountComponent.SLIDER_END_GENERAL;
        }

        // check if the initial value was changed by the user
        const defaultAmount = this.minAmount !== undefined ? this.minAmount : this.optionService.getOption("donation_value_default");
        const amountChanged = this.donation.amount !== defaultAmount && parseFloat(this.urlService.findGetParameter(this.urlService.PARAMETER_AMOUNT_PRESET)) !== parseFloat(this.donation.amount + '');

        // check if a button was presetted
        let buttonWasPresetted = false;

        // if the buttons are active
        if (this.hasButtons === true) {
            // put the buttonconfig together as an iterable array for the widget
            this.buttonConfig = [];
            const buttons = this.optionService.getOption('buttons');

            for (let i = 1; i < 10; i++) {
                if (typeof buttons['button' + i] !== "undefined" && buttons['button' + i]['amount'] && buttons['button' + i]['amount'] <= this.absolutMaximum) {
                    const amount = Number(Number(buttons['button' + i]['amount']).toFixed(2));

                    // show decimals if there is at least one
                    if (amount % 1 !== 0) {
                        this.showDecimals = true;
                    }

                    // detect amounts over 999
                    if (amount > 999) {
                        this.hasLargeAmount = true;
                    }

                    this.buttonConfig.push({
                        'amount': amount,
                        'label': 'amount.button' + i + '.label',
                    });

                    if (amount === this.donation.amount) {
                        // this button will be presetted
                        buttonWasPresetted = true;
                    }
                }
            }

            // preset the buttons, only if the user did not changed the amount yet
            if (!amountChanged) {
                this.switchbackToButtons();
            }
        }

        // show the slider if there are no buttons or if the amount was changed manually and no button is presetted
        this.showSlider = (!this.hasButtons || amountChanged === true) && buttonWasPresetted === false;

        const paymentMethods = this.optionService.getPaymentMethods();
        if (paymentMethods.length === 1 && paymentMethods[0] === "mbe4") {
            // disable the infotext in general
            this.disableInfotext = true;
        }

        // set start and end value of the slider
        this.sliderStart = parseInt(this.urlService.findGetParameter(this.urlService.PARAMETER_AMOUNT_MIN_PRESET)) || parseInt(this.optionService.getOption("donation_value_min"));
        this.sliderEnd = parseInt(this.urlService.findGetParameter(this.urlService.PARAMETER_AMOUNT_MAX_PRESET)) || parseInt(this.optionService.getOption("donation_value_max"));

        // validate min and max
        if (isNaN(this.sliderStart)) {
            this.sliderStart = this.optionService.getOption("donation_value_min");
        }
        if (isNaN(this.sliderEnd)) {
            this.sliderEnd = this.optionService.getOption("donation_value_max");
        }
        if ((this.sliderStart) > (this.sliderEnd)) {
            this.sliderStart = this.optionService.getOption("donation_value_min");
            this.sliderEnd = this.optionService.getOption("donation_value_max");
        }

        // avoind higher values in slider that allowed in project
        if (this.sliderEnd > this.absolutMaximum) {
          this.sliderEnd = this.absolutMaximum;
        }

        // overwrite the sliderStart if a minAmount is set
        if (this.minAmount !== undefined) {
            this.sliderStart = this.minAmount;
        }

        // set the value to the form elements, otherwise the form is not valid
        // add all the formControls we need in this Component
        this.form.addControl('amount', new FormControl(this.donation.amount, [Validators.min(this.sliderStart), Validators.max(this.absolutMaximum)]));
        this.form.addControl('donation_amount', new FormControl(this.donation.amount));

        // preset the slider only if it is shown
        if (this.showSlider) {
            this.valueChanged();
        }
    }

    /**
     * Returns the formatted amount with two or without decimals
     */
    getFormattedAmount(): string {
        if (this.donation.amount % 1 !== 0) {
            return this.donation.amount.toFixed(2);
        }
        return this.donation.amount.toFixed(0);
    }


    /**
     * Method is called every time the user changes manually the donation amount
     */
    valueChanged(newValue: string = null): void {
        // check if the value is an empty string and use a different error message
        if (newValue === "") {
            this.errorTextkey = 'amount.decimal.error';
            return;
        } else {
            this.errorTextkey = 'amount.error';
        }

        // check if the event value is available and set the donation amount
        if (newValue) {
            this.donation.amount = parseFloat(newValue.replace(',', '.'));
        }


        // set to default minimum if the value is to small
        if (this.donation.amount < this.sliderStart) {
            this.showInfotext = true;
            this.infoTextTextkey = 'amount.info.min';
            this.adjustInfotextHeight();

            this.form.get('amount').setValue(this.sliderStart);
            this.donation.amount = this.sliderStart;
            this.updateSliderposition();

            // stop here
            return;
        }

        // only update the slider to the given value if it is a correct value
        if (this.donation.amount > this.sliderEnd) {
            // hide the infotext
            this.showInfotext = false;
        }
        if (this.donation.amount >= this.absolutMaximum) {
            // show the infotext
            this.showInfotext = true;
            this.donation.amount = this.absolutMaximum;
        }

        // make sure the value is a number if it is changed manually
        this.donation.amount *= 1;

        this.updateSliderposition();
    }

    /**
     * recalculate slider value as oppsosite to sliderChanged()
     */
    updateSliderposition(): void {
        if (this.sliderEnd >= AmountComponent.SLIDER_NON_LINEAR_MIN) {
            const sliderValueLinear = (this.donation.amount - this.sliderStart) / (this.sliderEnd - this.sliderStart);
            this.sliderValue = (Math.pow(sliderValueLinear, 1 / AmountComponent.SLIDER_SPEED)) * 100;
        }
        else {
            this.sliderValue = Math.round(((this.donation.amount - this.sliderStart) / (this.sliderEnd - this.sliderStart)) * 100);
        }
    }

    /**
     * Eventhandler for changes on the slider value
     * @param event
     */
    sliderChanged(event): void {
        // value as float because we have a step of 0.5
        this.sliderValue = parseFloat(event.target.value);

        // calculate the non-linear amount based on the percentage and an exponantial funktion, SLIDER_SPEED de/increases the speed of the amount rise
        let valueLinear = Math.round(this.sliderStart + ((this.sliderEnd - this.sliderStart) * this.sliderValue / 100));
        let amount = valueLinear;
        if (this.sliderEnd >= AmountComponent.SLIDER_NON_LINEAR_MIN) {
            valueLinear /= this.sliderEnd;
            amount = Math.round((this.sliderEnd - 1) * Math.pow(valueLinear, AmountComponent.SLIDER_SPEED) + this.sliderStart);
            if (this.sliderValue === 0) {
                // special case. If the slider is set back to 0%, set the start value
                amount = this.sliderStart;
            }
        }

        // round the selected value to bigger sizes if the value is higher
        if (amount > this.absolutMaximum) {
            amount = Math.round(amount);
        }
        else if (amount > 10000) {
            amount = Math.round(amount / 500) * 500;
        }
        else if (amount > 5000) {
            amount = Math.round(amount / 100) * 100;
        }
        else if (amount > 1000) {
            amount = Math.round(amount / 50) * 50;
        }
        else if (amount > 500) {
            amount = Math.round(amount / 10) * 10;
        }
        else if (amount > 200) {
            amount = Math.round(amount / 5) * 5;
        }

        this.donation.amount = Math.min(amount, this.sliderEnd, this.absolutMaximum);
    }

    /**
     * Returns if the input is valid or not
     *
     * @returns {boolean}
     */
    public inputValid(): boolean {
        return this.form.controls['amount'].touched === false || this.form.controls['amount'].valid === true;
    }

    /**
     * Returns if the maximum of the sliders possible value is reached
     * @returns {boolean}
     */
    checkMaximum(): void {
        if(this.disableInfotext === true) {
            // do nothing
            return;
        }

        if (this.donation.amount === this.sliderEnd || this.donation.amount >= this.absolutMaximum) {
            this.showInfotext = true;
            this.infoTextTextkey = this.donation.amount >= this.absolutMaximum ? 'amount.info.max' : 'amount.info';

            this.adjustInfotextHeight();
        }
        else if (this.donation.amount !== this.sliderStart) {
            // hide infotext
            this.showInfotext = false;
        }
    }

    /**
     * set the height of the infotext bubble
     */
    adjustInfotextHeight(): void {
        setTimeout(() => {
            // repositioning the infotext
            if (this.amountInfotext) {
                const infotext = this.amountInfotext.nativeElement;
                const input = this.transactionAmount.nativeElement;
                this.infotextHeight = ((input.offsetHeight - infotext.offsetHeight) / 2) + "px";
            }
        }, 0);
    }

    /**
     * Creates the label for a single button, depending on its configuration and the availability of the extra label
     *
     * @param button
     */
    buttonLabel(button): string {
        let label = '';
        if (typeof button['label'] !== "undefined" && button['label'].length > 0) {
            label += '<small>' + button['label'] + '</small>';
        }
        label += '<strong>' + button['amount'] + ' ' + this.donation.currencySymbol + '</strong>';

        return label;
    }

    /**
     * Switch back from slider to buttons. Activate the nearest button
     */
    switchbackToButtons(): void {
        this.showSlider = false;

        // find nearest (smaller) button of the current value and activate this
        let diff = null;
        const presetAmount = this.donation.amount;
        // check if we need higher buttons and check the preset
        for (const button of this.buttonConfig) {
            // preselect button that is nearest to the default value
            if (diff === null || diff > Math.abs(button.amount - presetAmount)) {
                diff = Math.abs(button.amount - presetAmount);
                this.donation.amount = button.amount;
            }
        }
    }
}
